import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.js';
import { supportedLanguages } from '../config/languages.js';
import { fetchCurrentUser, signInUrl, signOutUrl } from '../utils/authClient.js';
import { isActiveLocalizedPath, localizePath, stripLanguageFromPath } from '../utils/localizedRoutes.js';
import { brandImages } from '../assets/brand/index.js';
import teamProfiles from '../profile/index.cjs';

const { teamMembers } = teamProfiles;

// The platform offers one application, the trading assistant: the button
// opens it in the app host, which starts the person's own container.
const platformAppPath = '/shinyproxy/app/smtm-frontend';

// The two parts of the IDD page, as anchors the page sets and labels from the
// navigation's language files.
const iddParts = [
  { anchor: 'legacy', label: 'iddLegacy' },
  { anchor: 'method', label: 'iddMethod' }
];

const Navigation = () => {
  const { t, setLanguage, currentLang } = useTranslation();
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTeamMenuOpen, setIsTeamMenuOpen] = useState(false);
  const [isIddMenuOpen, setIsIddMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [authState, setAuthState] = useState({ loading: true, user: null });
  const languageMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const linksRef = useRef(null);
  const adminButtonRef = useRef(null);
  const menuButtonRef = useRef(null);
  const adminButtonWidth = useRef(0);
  const [isCompact, setIsCompact] = useState(false);
  const [isAdminButtonHidden, setIsAdminButtonHidden] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setIsLanguageMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadAuth = async () => {
      try {
        const payload = await fetchCurrentUser();
        if (!cancelled) {
          setAuthState({ loading: false, user: payload.user });
        }
      } catch (error) {
        if (!cancelled) {
          setAuthState({ loading: false, user: null });
        }
      }
    };

    loadAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoginClick = () => {
    if (authState.user) {
      handleOpenApp();
    } else {
      // Sign-in is Keycloak's, reached through oauth2-proxy.
      window.location.assign(signInUrl());
    }
  };

  const handleLogout = () => {
    window.location.assign(signOutUrl());
  };

  const handleOpenApp = () => {
    window.location.assign(platformAppPath);
  };

  const isActiveLink = (path) => {
    const strippedPath = stripLanguageFromPath(location.pathname);
    if (path !== '/' && strippedPath.startsWith(`${path}/`)) {
      return true;
    }
    return isActiveLocalizedPath(location.pathname, path);
  };

  const toLocalizedPath = (path) => localizePath(path, currentLang);

  const isAdmin = Boolean(authState.user?.roles?.includes('admin'));

  // The links share the bar with the logo and the right side, which grows for a
  // signed-in user (admin entry, app button, user name) and with the language.
  // When they do not fit, the admin entry leaves the bar first (it stays in the
  // user menu); when that is not enough, the bar uses the compact menu instead
  // of letting the two overlap. The links stay rendered, out of sight, so their
  // width can be measured in either mode.
  useLayoutEffect(() => {
    const measure = () => {
      const links = linksRef.current;
      const row = links?.parentElement;
      if (!links || !row) return;
      const logo = row.firstElementChild;
      const right = links.nextElementSibling;
      // The admin entry's width is remembered while it is shown, so the bar can
      // be measured as if it were there when it is hidden.
      const adminButton = adminButtonRef.current;
      if (adminButton && adminButton.offsetWidth > 0) {
        adminButtonWidth.current = adminButton.offsetWidth + 8;
      }
      const signedInAdmin = Boolean(authState.user?.roles?.includes('admin'));
      const adminWidth = signedInAdmin && (adminButton || isAdminButtonHidden) ? adminButtonWidth.current : 0;
      const shownAdminWidth = adminButton && adminButton.offsetWidth > 0 ? adminWidth : 0;
      // The compact menu's button is only there while the links are not, so it
      // does not count against them.
      const menuButtonWidth = menuButtonRef.current ? menuButtonRef.current.offsetWidth + 8 : 0;
      const rightWithAdmin = (right?.offsetWidth || 0) - menuButtonWidth - shownAdminWidth + adminWidth;
      // The width the entries need, not the width the bar gives them: in the
      // full bar the links box stretches over all free space.
      const entries = [...links.children];
      const needed = entries.reduce((sum, entry) => sum + entry.offsetWidth, 0) + 4 * Math.max(entries.length - 1, 0);
      const available = row.clientWidth - (logo?.offsetWidth || 0) - rightWithAdmin - 24;
      setIsAdminButtonHidden(adminWidth > 0 && needed > available);
      setIsCompact(needed > available + adminWidth);
    };
    measure();
    // Entries and the right side change width when the company font arrives,
    // the session loads or the language changes; every change measures again.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    const links = linksRef.current;
    if (observer && links) {
      [...links.children, links.nextElementSibling, links.parentElement].filter(Boolean).forEach((element) => observer.observe(element));
    }
    document.fonts?.ready.then(measure);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [authState.user, authState.loading, currentLang, isAdminButtonHidden]);

  const languages = supportedLanguages.map((language) => ({
    ...language,
    name: t.nav?.languageNames?.[language.code] || language.code.toUpperCase()
  }));

  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  const linkClasses = (path) => `site-nav__link${isActiveLink(path) ? ' site-nav__link--active' : ''}`;

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    navigate(`${localizePath(location.pathname, lang)}${location.search}${location.hash}`);
    setIsLanguageMenuOpen(false);
  };

  const handleUserMenuLink = () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className={`site-nav fixed w-full z-50 transition-colors duration-300${scrollY > 20 ? ' site-nav--scrolled' : ''}`}>
        {/* The bar uses the whole width of the screen, so a wide monitor gives the links room; the
            measurement below alone decides between the links and the compact menu. */}
        <div className="w-full mx-auto px-4 lg:px-8">
          <div className="flex justify-between items-center h-16">
            
            {/* The logo is the home link. */}
            <Link to={toLocalizedPath('/')} className="site-nav__logo flex-shrink-0">
              <img src={brandImages.wordmark} alt={t.nav?.title} />
            </Link>
            
            {/* Navigation Links */}
            <div
              ref={linksRef}
              className={isCompact
                ? 'site-nav__links--measured flex items-center gap-1'
                : 'flex items-center gap-1 flex-1 justify-center min-w-0'}
              aria-hidden={isCompact ? 'true' : undefined}
            >
              <Link to={toLocalizedPath('/')} className={linkClasses('/')}>
                <span className="flex items-center space-x-1">
                  <span>{t.nav?.home}</span>
                </span>
              </Link>
              
              {/* Team: the entry leads to the overview, the menu to each member's profile. */}
              <div
                className="relative"
                onMouseEnter={() => setIsTeamMenuOpen(true)}
                onMouseLeave={() => setIsTeamMenuOpen(false)}
                onFocus={() => setIsTeamMenuOpen(true)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsTeamMenuOpen(false);
                  }
                }}
              >
                <Link
                  to={toLocalizedPath('/team')}
                  className={`${linkClasses('/team')} inline-flex items-center`}
                  aria-haspopup="true"
                  aria-expanded={isTeamMenuOpen}
                  onClick={() => setIsTeamMenuOpen(false)}
                >
                  <span className="flex items-center space-x-1">
                    <span>{t.nav?.team}</span>
                    <ChevronDown size={12} className={`transition-transform duration-200 ${isTeamMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </span>
                </Link>
                {isTeamMenuOpen && (
                  <div className="absolute left-0 top-full pt-2 z-[100]">
                    <div className="site-nav__menu">
                      {teamMembers.map((member) => (
                        <Link
                          key={member.slug}
                          to={toLocalizedPath(`/team/${member.slug}`)}
                          className={`site-nav__menu-item${isActiveLink(`/team/${member.slug}`) ? ' site-nav__menu-item--active' : ''}`}
                          onClick={() => setIsTeamMenuOpen(false)}
                        >
                          {member.displayName}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <Link to={toLocalizedPath('/veridion')} className={linkClasses('/veridion')}>
                <span className="flex items-center space-x-1">
                  <span>{t.nav?.veridion}</span>
                </span>
              </Link>

              {/* IDD: the page has two parts, the modernisation story and the method; the menu jumps to either. */}
              <div
                className="relative"
                onMouseEnter={() => setIsIddMenuOpen(true)}
                onMouseLeave={() => setIsIddMenuOpen(false)}
                onFocus={() => setIsIddMenuOpen(true)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsIddMenuOpen(false);
                  }
                }}
              >
                <Link
                  to={toLocalizedPath('/agenticdev')}
                  className={`${linkClasses('/agenticdev')} inline-flex items-center`}
                  aria-haspopup="true"
                  aria-expanded={isIddMenuOpen}
                  onClick={() => setIsIddMenuOpen(false)}
                >
                  <span className="flex items-center space-x-1">
                    <span className="whitespace-nowrap">{t.nav?.agenticdev}</span>
                    <ChevronDown size={12} className={`transition-transform duration-200 ${isIddMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </span>
                </Link>
                {isIddMenuOpen && (
                  <div className="absolute left-0 top-full pt-2 z-[100]">
                    <div className="site-nav__menu">
                      {iddParts.map((part) => (
                        <Link
                          key={part.anchor}
                          to={`${toLocalizedPath('/agenticdev')}#${part.anchor}`}
                          className="site-nav__menu-item"
                          onClick={() => setIsIddMenuOpen(false)}
                        >
                          {t.nav?.[part.label]}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to={toLocalizedPath('/kyveil')} className={linkClasses('/kyveil')}>
                <span className="flex items-center space-x-1">
                  <span>{t.nav?.kyveil}</span>
                </span>
              </Link>

              <Link to={toLocalizedPath('/teaching')} className={linkClasses('/teaching')}>
                <span className="flex items-center space-x-1">
                  <span>{t.nav?.teaching}</span>
                </span>
              </Link>
              
              <Link to={toLocalizedPath('/finance')} className={linkClasses('/finance')}>
                <span className="flex items-center space-x-1">
                  <span>{t.nav?.finance}</span>
                </span>
              </Link>

              <Link to={toLocalizedPath('/quantum')} className={linkClasses('/quantum')}>
                <span className="flex items-center space-x-1">
                  <span>{t.nav?.quantum}</span>
                </span>
              </Link>
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              
              {/* Language Selector */}
              <div className="relative flex items-center" ref={languageMenuRef}>
                <button 
                  type="button"
                  className="site-nav__control site-nav__control--lang"
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  aria-label={currentLanguage?.name}
                >
                  <img src={currentLanguage?.flag} alt={currentLanguage?.name} className="w-5 h-3.5 object-cover rounded-[1px]" />
                  <span>{currentLang?.toUpperCase()}</span>
                  <ChevronDown size={13} className={`transition-transform duration-200 ${isLanguageMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLanguageMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 z-[100]">
                    <div className="site-nav__menu">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`site-nav__menu-item flex items-center gap-2${currentLang === lang.code ? ' site-nav__menu-item--active' : ''}`}
                        >
                          <img src={lang.flag} alt="" className="w-5 h-3.5 object-cover rounded-[1px]" />
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Login Button */}
              {authState.user ? (
                <div className="flex items-center gap-2">
                  {/* Role-bound entry. The target is protected independently. */}
                  {isAdmin && !isAdminButtonHidden ? (
                    <Link
                      ref={adminButtonRef}
                      to={toLocalizedPath('/admin')}
                      className="hidden md:inline-flex homepage-button-secondary site-nav__button"
                    >
                      {t.nav?.admin}
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleOpenApp}
                    className="homepage-button-primary site-nav__button"
                  >
                    {t.nav?.openApp}
                  </button>
                  <div className="relative flex items-center" ref={userMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsUserMenuOpen((current) => !current)}
                      className="site-nav__control"
                    >
                      <span className="max-w-[10rem] truncate">{authState.user?.username || t.nav?.account}</span>
                      <ChevronDown size={14} className={`transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isUserMenuOpen ? (
                      <div className="absolute right-0 mt-2 min-w-[12rem] z-[100]">
                        <div className="site-nav__menu">
                          <Link
                            to={toLocalizedPath('/account')}
                            className="site-nav__menu-item"
                            onClick={handleUserMenuLink}
                          >
                            {t.nav?.account}
                          </Link>
                          {isAdmin ? (
                            <Link
                              to={toLocalizedPath('/admin')}
                              className="site-nav__menu-item"
                              onClick={handleUserMenuLink}
                            >
                              {t.nav?.admin}
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            className="site-nav__menu-item"
                            onClick={() => {
                              setIsUserMenuOpen(false);
                              handleLogout();
                            }}
                          >
                            {t.nav?.logout}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleLoginClick}
                  className="homepage-button-primary site-nav__button"
                >
                  {authState.loading ? t.auth?.validating : t.nav?.login}
                </button>
              )}

              {/* Mobile Menu Button */}
              {isCompact && (
                <button
                  ref={menuButtonRef}
                  type="button"
                  className="site-nav__control"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  {isMobileMenuOpen ? (
                    <X size={18} className="text-text-primary" />
                  ) : (
                    <Menu size={18} className="text-text-primary" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && isCompact && (
        <div className="fixed inset-0 z-40">
          <div className="fixed inset-0 bg-text-primary/30" onClick={() => setIsMobileMenuOpen(false)} />
          
          <div className="site-nav__mobile fixed top-16 left-4 right-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="py-3">
              <Link 
                to={toLocalizedPath('/')}
                className={`site-nav__mobile-item${isActiveLink('/') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav?.home}
              </Link>
              
              <Link 
                to={toLocalizedPath('/team')}
                className={`site-nav__mobile-item${isActiveLocalizedPath(location.pathname, '/team') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav?.team}
              </Link>

              {teamMembers.map((member) => (
                <Link
                  key={member.slug}
                  to={toLocalizedPath(`/team/${member.slug}`)}
                  className={`site-nav__mobile-item site-nav__mobile-item--sub${isActiveLink(`/team/${member.slug}`) ? ' site-nav__mobile-item--active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {member.displayName}
                </Link>
              ))}
              
              <Link 
                to={toLocalizedPath('/veridion')}
                className={`site-nav__mobile-item${isActiveLink('/veridion') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                  <span>{t.nav?.veridion}</span>
              </Link>

              <Link 
                to={toLocalizedPath('/agenticdev')}
                className={`site-nav__mobile-item${isActiveLink('/agenticdev') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                  <span>{t.nav?.agenticdev}</span>
              </Link>

              {iddParts.map((part) => (
                <Link
                  key={part.anchor}
                  to={`${toLocalizedPath('/agenticdev')}#${part.anchor}`}
                  className="site-nav__mobile-item site-nav__mobile-item--sub"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t.nav?.[part.label]}
                </Link>
              ))}

              <Link 
                to={toLocalizedPath('/kyveil')}
                className={`site-nav__mobile-item${isActiveLink('/kyveil') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                  <span>{t.nav?.kyveil}</span>
              </Link>

              <Link 
                to={toLocalizedPath('/teaching')}
                className={`site-nav__mobile-item${isActiveLink('/teaching') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                  <span>{t.nav?.teaching}</span>
              </Link>
              
              <Link 
                to={toLocalizedPath('/finance')}
                className={`site-nav__mobile-item${isActiveLink('/finance') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                  <span>{t.nav?.finance}</span>
              </Link>

              <Link 
                to={toLocalizedPath('/quantum')}
                className={`site-nav__mobile-item${isActiveLink('/quantum') ? ' site-nav__mobile-item--active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                  <span>{t.nav?.quantum}</span>
              </Link>

              {authState.user ? (
                <Link 
                  to={toLocalizedPath('/account')}
                  className={`site-nav__mobile-item${isActiveLink('/account') ? ' site-nav__mobile-item--active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                    <span>{t.nav?.account}</span>
                </Link>
              ) : null}

              {isAdmin ? (
                <Link 
                  to={toLocalizedPath('/admin')}
                  className={`site-nav__mobile-item${isActiveLink('/admin') ? ' site-nav__mobile-item--active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                    <span>{t.nav?.admin}</span>
                </Link>
              ) : null}

              {authState.user ? (
                <>
                  <button
                    type="button"
                    className="site-nav__mobile-item"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleOpenApp();
                    }}
                  >
                    {t.nav?.openApp}
                  </button>
                  <button
                    type="button"
                    className="site-nav__mobile-item"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    {t.nav?.logout}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="site-nav__mobile-item"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleLoginClick();
                  }}
                >
                  {t.nav?.login}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
